import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bold, Italic } from "lucide-react";

export const FONT_FAMILIES = {
  sans: "Arial, Helvetica, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  script: "'Brush Script MT', 'Segoe Script', cursive",
  mono: "'Courier New', monospace",
};

export const startEdits = (words) => Object.fromEntries(words.map((w) => [w.id, {
  text: w.text, font: w.font, size: Math.max(6, Math.round((w.box[3] - w.box[1]) * 0.8)),
  color: w.color, background: w.background, bold: w.bold, italic: w.italic,
}]));

const changed = (w, e, base) => e && JSON.stringify(e) !== JSON.stringify(base[w.id]);

// Paints over each changed word with its background, then writes the new words in their style.
export function paintEdited(canvas, img, words, edits, base) {
  canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  for (const w of words) {
    const e = edits[w.id];
    if (!changed(w, e, base)) continue;
    const [x0, y0, x1, y1] = w.box;
    const pad = Math.max(2, (y1 - y0) * 0.12);
    ctx.fillStyle = e.background;
    ctx.fillRect(x0 - pad, y0 - pad, x1 - x0 + pad * 2, y1 - y0 + pad * 2);
    if (!e.text) continue;
    ctx.fillStyle = e.color;
    ctx.font = `${e.italic ? "italic " : ""}${e.bold ? "bold " : ""}${e.size}px ${FONT_FAMILIES[e.font] || FONT_FAMILIES.sans}`;
    ctx.textBaseline = "middle";
    ctx.fillText(e.text, x0, (y0 + y1) / 2);
  }
}

export const Preview = ({ src, words, edits, base, selected, onSelect, canvasRef }) => {
  const imgRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => { if (ready) paintEdited(canvasRef.current, imgRef.current, words, edits, base); }, [ready, words, edits, base, canvasRef]);
  return (
    <div className="relative inline-block max-w-full bg-[repeating-conic-gradient(#f1f5f9_0_25%,#fff_0_50%)] bg-[length:16px_16px]" data-testid="pdf-ai-preview">
      <img ref={imgRef} src={src} alt="" className="hidden" onLoad={() => setReady(true)} />
      <canvas ref={canvasRef} className="block max-w-full h-auto" />
      {ready && words.map((w) => {
        const W = imgRef.current.naturalWidth, H = imgRef.current.naturalHeight;
        return (
          <button key={w.id} type="button" onClick={() => onSelect(w.id)} title={w.text}
            className={`absolute border ${selected === w.id ? "border-sky-500 bg-sky-400/20" : "border-amber-400/70 hover:bg-amber-300/20"}`}
            style={{ left: `${(w.box[0] / W) * 100}%`, top: `${(w.box[1] / H) * 100}%`, width: `${((w.box[2] - w.box[0]) / W) * 100}%`, height: `${((w.box[3] - w.box[1]) / H) * 100}%` }}
            data-testid={`pdf-ai-box-${w.id}`} />
        );
      })}
    </div>
  );
};

export const WordRow = ({ word, edit, selected, onSelect, onChange }) => {
  const set = (k, v) => onChange({ ...edit, [k]: v });
  const toggle = (k, Icon) => (
    <button type="button" onClick={() => set(k, !edit[k])} className={`p-1 rounded border ${edit[k] ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}
      data-testid={`pdf-ai-${k}-${word.id}`}><Icon className="w-3 h-3" /></button>
  );
  return (
    <div onClick={onSelect} className={`rounded border p-2 space-y-1.5 ${selected ? "border-sky-500 bg-sky-50" : "border-slate-200"}`} data-testid={`pdf-ai-word-${word.id}`}>
      <input value={edit.text} onChange={(e) => set("text", e.target.value)} className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        data-testid={`pdf-ai-text-${word.id}`} />
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600">
        <select value={edit.font} onChange={(e) => set("font", e.target.value)} className="border border-slate-300 rounded px-1 py-0.5" data-testid={`pdf-ai-font-${word.id}`}>
          {Object.keys(FONT_FAMILIES).map((f) => <option key={f} value={f}>{f[0].toUpperCase() + f.slice(1)}</option>)}
        </select>
        <input type="number" min={4} max={600} value={edit.size} onChange={(e) => set("size", Math.max(4, Number(e.target.value) || 4))}
          className="w-14 border border-slate-300 rounded px-1 py-0.5" title="Size" data-testid={`pdf-ai-size-${word.id}`} />
        <label className="flex items-center gap-0.5" title="Text colour">A<input type="color" value={edit.color} onChange={(e) => set("color", e.target.value)} className="w-5 h-5 p-0 border-0" data-testid={`pdf-ai-color-${word.id}`} /></label>
        <label className="flex items-center gap-0.5" title="Background behind the words">Bg<input type="color" value={edit.background} onChange={(e) => set("background", e.target.value)} className="w-5 h-5 p-0 border-0" data-testid={`pdf-ai-bg-${word.id}`} /></label>
        {toggle("bold", Bold)}
        {toggle("italic", Italic)}
      </div>
    </div>
  );
};

export const useChangedCount = (words, edits, base) => useMemo(() => words.filter((w) => changed(w, edits[w.id], base)).length, [words, edits, base]);
